export type AppRegion = 'us' | 'ca';

const REGION_HOSTS: Record<AppRegion, string> = {
  us: 'motzulabs.com',
  ca: 'motzulabs.ca',
};

export function detectRegionFromHostname(hostname: string): AppRegion {
  const normalized = hostname.toLowerCase();

  if (
    normalized.endsWith('.ca') ||
    normalized.includes('motzu-labs-ca.web.app') ||
    normalized.includes('motzu-labs-ca.firebaseapp.com')
  ) {
    return 'ca';
  }

  return 'us';
}

export function getCurrentRegion(): AppRegion {
  if (typeof window === 'undefined') {
    return 'us';
  }

  return detectRegionFromHostname(window.location.hostname);
}

export function getRegionDisplay(region: AppRegion): { label: string; country: string; flag: string } {
  if (region === 'ca') {
    return { label: 'Canada', country: 'Canada', flag: '🇨🇦' };
  }
  return { label: 'United States', country: 'United States', flag: '🇺🇸' };
}

export function buildRegionLoginUrl(region: AppRegion): string {
  if (typeof window === 'undefined') {
    return '/login';
  }

  const { pathname, search, hash, hostname, protocol } = window.location;
  const isLocal =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');

  if (isLocal) {
    return `${pathname}${search}${hash}`;
  }

  const targetHost = REGION_HOSTS[region];
  return `${protocol}//${targetHost}${pathname}${search}${hash}`;
}

/**
 * Build a URL for switching to the other regional app, preserving the current
 * path. Returns null when no redirect is needed (already on the target
 * region's domain, or running locally where both regions share one host).
 */
export function buildRegionSwitchUrl(region: AppRegion, language: 'en' | 'fr'): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const { pathname, search, hash, hostname, protocol } = window.location;
  const isLocal =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');

  if (isLocal || detectRegionFromHostname(hostname) === region) {
    return null;
  }

  const params = new URLSearchParams(search);
  params.set('lang', language);
  params.set('region', region === 'ca' ? 'CA' : 'US');

  const targetHost = REGION_HOSTS[region];
  return `${protocol}//${targetHost}${pathname}?${params.toString()}${hash}`;
}

